/******************************************************
 * This is the form used by users to edit or arr a new
 * billing or shipping address in the checkout page.
 * ************************************************
 *
 * @format */

import {CartCheckoutContext} from 'frontier/cart/components/cart/cart_checkout_context';
import FormAddressInput from 'frontier/cart/components/addresses/form_address_input';
import {useAddressForm} from 'frontier/cart/components/addresses/use_address_form';
import {AddressAutocomplete, InputWithErrorHandler} from 'frontier-form';
import {Alert, Button, Checkbox} from '@sttark/design-system';
import React, {useState, useContext} from 'react';
import {ErrorDisplay} from 'frontier-utilities';

export function CartCheckoutAddressForm(props) {
    const ctx = useContext(CartCheckoutContext);
    const {
        onFormPhoneInputChange,
        onFormInputChange,
        addressFormErrors,
        onFormUpdate,
        onFormChange,
        formData,
        isSaving,
        onSave
    } = useAddressForm({...props.formData, onSaveComplete: props.onSaveComplete});
    const [includesProofEmail, setIncludesProofEmail] = useState(formData.proof_email);

    return (
        <div className="cart-form bg-structure-white">
            {/*header*/}
            <div className="cart-form_header">
                <h5 className="header_heading m-0">{props.formTitle}</h5>
                <div className="header_buttons d-flex align-items-center justify-content-center">
                    <div>
                        <Button label="Cancel" onClick={props.handleClose} secondary large />
                    </div>
                    <div>
                        <Button onClick={onSave} disabled={isSaving} label="Save" primary large />
                    </div>
                </div>
            </div>
            <div className="cart-form_section">
                <div className="cart-form_form">
                    {/* notification of new address setting as default*/}
                    {formData.is_default_address && (
                        <p className="font-body-sm mb-5">
                            This address will be automatically saved as the default address for
                            subsequent orders.
                        </p>
                    )}
                    <div className="form-address-input-34kd">
                        <FormAddressInput
                            defaultCompany={formData.company}
                            defaultAddress={formData.formatted_address}
                            onUpdate={onFormUpdate}
                            renderAddressAutocomplete={props => {
                                return (
                                    <div className="form_property form-group tw-p-0">
                                        <AddressAutocomplete {...props} />
                                    </div>
                                );
                            }}
                            renderInputComponent={props => {
                                return (
                                    <InputWithErrorHandler
                                        {...props}
                                        newestds={true}
                                        label={
                                            props.inputProps?.name === 'company'
                                                ? 'Company'
                                                : 'Address'
                                        }
                                    />
                                );
                            }}
                        />
                    </div>

                    <div className="form_property form-group d-flex tw-p-0">
                        {/* full name and company name */}
                        <div>
                            <InputWithErrorHandler
                                newestds={true}
                                placeholder="*Enter full name"
                                label="*Full Name"
                                className="form-control form-control-sm"
                                value={formData.full_name}
                                error={addressFormErrors}
                                errorPath={
                                    addressFormErrors?.first_name ? 'first_name' : 'last_name'
                                }
                                onChange={onFormInputChange.bind(this, 'full_name')}
                            />
                        </div>

                        <div style={{marginLeft: '18px'}}>
                            <InputWithErrorHandler
                                newestds={true}
                                label="*Phone Number"
                                placeholder="*Enter phone number"
                                className="form-control form-control-sm"
                                value={formData.phone}
                                error={addressFormErrors}
                                errorPath={'phone'}
                                onChange={onFormPhoneInputChange.bind(this, 'phone')}
                            />
                        </div>
                    </div>

                    {/* email */}
                    <div className="m-0 form-group col-6">
                        <InputWithErrorHandler
                            newestds={true}
                            label="*Email"
                            placeholder="*Enter email"
                            className="form-control form-control-sm"
                            value={formData.email}
                            error={addressFormErrors}
                            errorPath={'email'}
                            onChange={onFormInputChange.bind(this, 'email')}
                        />
                    </div>

                    {!includesProofEmail && formData.is_shipping_address && (
                        <p
                            className="add-second-address add-second-proof-email color-action-primary font-body-md"
                            onClick={() => setIncludesProofEmail(true)}>
                            + Add additional proof recipient email addresses
                        </p>
                    )}

                    {includesProofEmail && formData.is_shipping_address && (
                        <div className="form_property form_property--proof-email row tw-p-0">
                            <div className="m-0 form-group col-12">
                                <InputWithErrorHandler
                                    newestds={true}
                                    label="Proof Email"
                                    placeholder="Enter Proof email"
                                    className="form-control form-control-sm"
                                    value={formData.proof_email}
                                    error={addressFormErrors}
                                    errorPath={'proof_email'}
                                    onChange={onFormInputChange.bind(this, 'proof_email')}
                                />
                            </div>
                            <strong className="mx-0 px-0 mt-4">
                                To add multiple proofing recipients, separate email addresses with
                                commas
                            </strong>
                        </div>
                    )}

                    {/* is default address */}
                    <div className="d-flex">
                        <div className="me-2">
                            <Checkbox
                                small
                                bold={formData.is_default_address}
                                label={
                                    formData.is_default_address
                                        ? 'This is your default address'
                                        : 'Set as default address'
                                }
                                name="is_default_address"
                                value={formData.is_default_address}
                                checked={formData.is_default_address}
                                onChange={onFormChange.bind(this, 'is_default_address')}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {addressFormErrors && !isSaving && (
                <Alert title="Please validate your address!" positive={false}>
                    <ErrorDisplay error={addressFormErrors} />
                </Alert>
            )}
        </div>
    );
}
